// import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { alpha, styled, useTheme as useMuiTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ExportCsvButton from "../../components/ExportCsvButton";
import { useTransactions, useExportTransactions, useGenerateReceipt, useSendReceiptEmail, useTransactionStats } from "../../hooks/useTransactions";
import TableSkeleton from "../../components/TableSkeleton";
import EmailIcon from "@mui/icons-material/Email";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReactApexChart from "react-apexcharts";

// Styled components
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let bg, color;

  // Check if we're in light mode
  const isLightMode = theme.palette.mode === "light";

  switch (status?.toLowerCase()) {
    case "completed":
    case "success":
      bg = isLightMode
        ? alpha(theme.palette.success.main, 0.1)
        : alpha(theme.palette.success.main, 0.25);
      color = theme.palette.success.main;
      break;
    case "failed":
      bg = isLightMode
        ? alpha(theme.palette.error.main, 0.1)
        : alpha(theme.palette.error.main, 0.2);
      color = theme.palette.error.main;
      break;
    case "pending":
      bg = isLightMode
        ? alpha(theme.palette.warning.main, 0.1)
        : alpha(theme.palette.warning.main, 0.2);
      color = theme.palette.warning.main;
      break;
    case "expired":
      bg = isLightMode ? "transparent" : alpha(theme.palette.info.main, 0.2);
      color = isLightMode ? theme.palette.info.main : theme.palette.info.main;
      break;
    case "refunded":
      bg = isLightMode ? "transparent" : alpha(theme.palette.secondary.main, 0.2);
      color = isLightMode ? theme.palette.secondary.main : theme.palette.secondary.main;
      break;
    case "cancelled":
      bg = isLightMode ? "transparent" : alpha(theme.palette.grey[500], 0.2);
      color = isLightMode ? theme.palette.grey[700] : theme.palette.grey[300];
      break;
    default:
      bg = isLightMode ? "transparent" : alpha(theme.palette.text.primary, 0.2);
      color = isLightMode
        ? theme.palette.text.primary
        : theme.palette.text.primary;
  }
  return {
    backgroundColor: bg,
    color,
    borderRadius: 4,
    height: 24,
    fontSize: "0.80rem",
  };
});
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.action.disabledBackground, 0.1),
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(TextField)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  },
}));

const TimeFilterButton = styled(Button)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha(theme.palette.action.disabledBackground, 0.2),
  color: active ? theme.palette.common.white : theme.palette.text.primary,
  borderRadius: 20,
  textTransform: "none",
  padding: "4px 16px",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : alpha(theme.palette.action.disabledBackground, 0.3),
  },
}));

// Updated mock data with custom images

const Transaction = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [timeFilter, setTimeFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    "Transaction ID": true,
    User: true,
    Product: true,
    Date: true,
    Amount: true,
    "Payment Method": true,
    Status: true,
    Receipt: true,
    Email: true,
  });

  // Map tab values to API status filters
  const tabValueMap = {
    0: null, // All
    1: "completed", // Successful
    2: "failed", // Failed
    3: "refunded", // Refunded
  };

  // Fetch transactions with current filters
  const { data: transactions = {}, isLoading, error } = useTransactions({
    page,
    limit: rowsPerPage,
    status: tabValueMap[tabValue],
    search: searchTerm,
    dateFilter: timeFilter.toLowerCase(),
  });

  const { data: statsData } = useTransactionStats({
    dateFilter: timeFilter.toLowerCase(),
  });
  const stats = statsData?.data || {};

  const exportMutation = useExportTransactions();

  const theme = useMuiTheme();

  const visibleColumns = {
    User: true,
    Date: true,
    Amount: true,
    "Payment Method": true,
    Status: true,
    Receipt: true,
    Revenue: true,
  };

  const handleTabChange = (_, v) => {
    setTabValue(v);
    setPage(1); // Reset to page 1 when tab changes
  };

  const handleRowsChange = (e) => setRowsPerPage(e.target.value);
  const handleMoreClick = (e, row) => {
    setCurrentRow(row);
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const generateReceiptMutation = useGenerateReceipt();
  const sendReceiptEmailMutation = useSendReceiptEmail();

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleGenerateReceipt = async (row) => {
    try {
      setAnchorEl(null);
      const data = await generateReceiptMutation.mutateAsync(row._id || row.id);
      setReceiptData(data.data);
      setReceiptDialogOpen(true);
    } catch (error) {
      console.error("Failed to generate receipt:", error);
      setSnackbar({
        open: true,
        message: "Failed to generate receipt",
        severity: "error",
      });
    }
  };

  const handleSendEmailReceipt = async (row) => {
    try {
      setAnchorEl(null);
      await sendReceiptEmailMutation.mutateAsync(row._id || row.id);
      setSnackbar({
        open: true,
        message: "Receipt sent successfully to user's email",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to send email receipt:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to send email receipt",
        severity: "error",
      });
    }
  };

  const handleDownloadReceipt = async (row) => {
    try {
      setAnchorEl(null);
      const response = await generateReceiptMutation.mutateAsync(row._id || row.id);
      const receiptData = response.data;

      const htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'><title>Receipt</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
          .receipt-container { max-width: 800px; margin: auto; border: 1px solid #eee; padding: 30px; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #0048e5; padding-bottom: 20px; }
          .logo-img { width: 220px; margin-bottom: 15px; }
          .receipt-title { font-size: 32px; font-weight: 900; color: #0048e5; text-transform: uppercase; letter-spacing: 2px; margin: 0; }
          
          .grid-container { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          .section-title { font-size: 16px; color: #0048e5; text-transform: uppercase; font-weight: 800; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 20px; margin-top: 30px; }
          
          .info-table { width: 100%; border-collapse: collapse; }
          .info-label { color: #666; font-size: 14px; width: 180px; padding: 8px 0; font-weight: 600; }
          .info-value { font-weight: 700; font-size: 14px; padding: 8px 0; color: #111; }
          
          .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .items-table th { background-color: #f8faff; color: #0048e5; text-align: left; padding: 12px; font-size: 14px; border-bottom: 2px solid #eee; }
          .items-table td { padding: 12px; border-bottom: 1px solid #eee; font-size: 14px; }
          
          .amount-summary { margin-top: 40px; width: 100%; }
          .amount-box { float: right; width: 300px; background: #f8faff; border: 2px dashed #0048e5; padding: 25px; border-radius: 12px; text-align: center; }
          .amount-label { color: #0048e5; font-size: 14px; font-weight: 800; margin-bottom: 10px; text-transform: uppercase; }
          .amount-value { font-size: 36px; font-weight: 900; color: #0048e5; margin: 0; }
          .clear { clear: both; }
          
          .footer { text-align: center; font-size: 12px; color: #888; margin-top: 60px; border-top: 1px solid #eee; padding-top: 30px; }
          .footer-links { color: #0048e5; font-weight: 700; text-decoration: none; margin: 0 10px; }
          .status-badge { display: inline-block; padding: 6px 15px; border-radius: 20px; font-weight: 800; font-size: 12px; text-transform: uppercase; }
          .status-completed { background-color: #e6f4ea; color: #1e7e34; }
          .status-failed { background-color: #fce8e6; color: #d93025; }
        </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              <img src="https://cdn.clikkle.com/images/clikkle/logo/2025/Clikkle%20Blue%20(%20Black%20Text).png" class="logo-img" />
              <h1 class="receipt-title">Payment Receipt</h1>
            </div>

            <div class="section-title">Transaction Information</div>
            <table class="info-table">
              <tr><td class="info-label">Transaction ID</td><td class="info-value">${receiptData.transactionId || 'N/A'}</td></tr>
              <tr><td class="info-label">Transaction Date</td><td class="info-value">${new Date(receiptData.date).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}</td></tr>
              <tr><td class="info-label">Payment Status</td><td class="info-value"><span class="status-badge ${receiptData.status === 'completed' ? 'status-completed' : 'status-failed'}">${receiptData.status?.toUpperCase()}</span></td></tr>
              <tr><td class="info-label">Payment Method</td><td class="info-value" style="text-transform: capitalize;">${receiptData.paymentMethod?.replace('_', ' ') || 'Stripe'} ${receiptData.card ? `(${receiptData.card.brand?.toUpperCase()} **** ${receiptData.card.last4})` : ''}</td></tr>
            </table>

            <div class="section-title">Customer Details</div>
            <table class="info-table">
              <tr><td class="info-label">Customer Name</td><td class="info-value">${receiptData.user?.name || 'Unknown User'}</td></tr>
              <tr><td class="info-label">Email Address</td><td class="info-value">${receiptData.user?.email || 'N/A'}</td></tr>
              ${receiptData.user?.recoveryEmail ? `<tr><td class="info-label">Recovery Email</td><td class="info-value">${receiptData.user.recoveryEmail}</td></tr>` : ''}
            </table>

            <div class="section-title">Purchase Details</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Product</th>
                  <th style="text-align: right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>${receiptData.planName || 'N/A'} Plan</strong><br/>
                    <small style="color: #666;">${receiptData.description || 'Subscription Service'}</small>
                  </td>
                  <td>${receiptData.productName || 'Clikkle Service'}</td>
                  <td style="text-align: right; font-weight: 700;">${receiptData.currency} ${receiptData.amount?.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            <div class="amount-summary">
              <div class="amount-box">
                <div class="amount-label">Total Amount Paid</div>
                <h2 class="amount-value">${receiptData.currency} ${receiptData.amount?.toFixed(2)}</h2>
              </div>
              <div class="clear"></div>
            </div>

            <div class="footer">
              <p style="font-weight: 700; margin-bottom: 5px;">Thank you for your business!</p>
              <p>This is a computer-generated document. For support, please reach out to us.</p>
              <p>
                <span class="footer-links">support.clikkle.com</span> | 
                <span class="footer-links">clikkle.com</span>
              </p>
              <p style="margin-top: 20px;">© ${new Date().getFullYear()} Clikkle Technologies. All rights reserved.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_${receiptData.transactionId || 'Transaction'}.doc`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download receipt:", error);
    }
  };
  const handleTimeChange = (f) => {
    setTimeFilter(f);
    setPage(1); // Reset to page 1 when time filter changes
  };
  const handlePageChange = (_, v) => setPage(v);
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to page 1 when search changes
  };

  // Filter dialog handlers - updated to match the first code
  const handleFilterDialogOpen = () => setFilterDialogOpen(true);
  const handleFilterDialogClose = () => setFilterDialogOpen(false);

  const handleFilterChange = (key) => {
    setFilterConfig((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Get filtered transactions from API
  const getFilteredTransactions = () => {
    console.log("Transactions data:", transactions);
    return transactions.data || [];
  };

  // Get export parameters
  const getExportParams = () => {
    return {
      status: tabValueMap[tabValue],
      search: searchTerm,
      dateFilter: timeFilter.toLowerCase(),
    };
  };

  const filtered = getFilteredTransactions();
  const totalPages = transactions.pagination?.pages || 1;

  // Generate table headers based on filterConfig
  const getTableHeaders = () => {
    const headerMapping = {
      "Transaction ID": "ID",
      User: "User",
      Product: "Product",
      Date: "Date",
      Amount: "Amount",
      "Payment Method": "Method",
      Status: "Status",
      Receipt: "Receipt",
    };

    return Object.entries(filterConfig)
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => headerMapping[key])
      .filter((header) => header); // Filter out undefined headers
  };

  // Function to render table cell based on header - updated for new API structure
  const renderTableCell = (row, header) => {
    switch (header) {
      case "ID":
        return <TableCell key={header}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {row.transactionId || (row._id ? `...${row._id.slice(-6)}` : 'N/A')}
          </Typography>
        </TableCell>;
      case "User":
        return (
          <TableCell key={header}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar src={row?.user?.avatar} sx={{ width: 24, height: 24 }} />
              <Box>
                <Typography variant="body2">{row?.user?.name || 'Unknown User'}</Typography>
                <Typography variant="caption" color="text.secondary">{row?.user?.email || 'N/A'}</Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate(`/users?userId=${row.userId}&tab=transactions`)}
                sx={{ ml: 0.5, color: theme.palette.primary.main }}
                title="View User Details"
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </TableCell>
        );
      case "Product":
        return (
          <TableCell key={header}>
            <Box>
              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}>
                {row.productName || row.type || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.planName || (row.subscriptionId?.planName) || 'N/A'}
              </Typography>
            </Box>
          </TableCell>
        );
      case "Date":
        return (
          <TableCell key={header}>
            <Typography variant="body2">{new Date(row.paymentDate || row.createdAt).toLocaleDateString()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(row.paymentDate || row.createdAt).toLocaleTimeString()}
            </Typography>
          </TableCell>
        );
      case "Amount":
        return <TableCell key={header}>
          <Typography variant="body2" fontWeight="bold">
            ${row?.amount?.toFixed(2)}
          </Typography>
        </TableCell>;
      case "Method":
        return <TableCell key={header}>
          <Chip
            label={row.paymentMethod?.replace('_', ' ') || 'Stripe'}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />
        </TableCell>;
      case "Status":
        const statusValue = row?.status || row?.regulated_status || 'completed';
        return (
          <TableCell key={header}>
            <StatusChip
              status={statusValue}
              label={statusValue.charAt(0).toUpperCase() + statusValue.slice(1)}
            />
          </TableCell>
        );
      case "Receipt":
        return (
          <TableCell key={header} align="center">
            <IconButton
              onClick={(e) => handleMoreClick(e, row)}
              sx={{
                color: theme.palette.text.primary,
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.2),
                },
              }}
              size="small"
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </TableCell>
        );
      case "Email":
        return (
          <TableCell key={header} align="center">
            <IconButton
              onClick={() => handleSendEmailReceipt(row)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
              size="small"
              title="Email Receipt"
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </TableCell>
        );
      default:
        return <TableCell key={header}></TableCell>;
    }
  };

  // Get headers for display
  const tableHeaders = getTableHeaders();
  const isDarkMode = theme.palette.mode === "dark";

  // Get CSV columns for export based on filterConfig
  const getCsvColumns = () => {
    const keyMapping = {
      "Transaction ID": "id",
      User: "user.name",
      Date: "date",
      Amount: "amount",
      "Payment Method": "paymentMethod",
      Status: "status",
      Receipt: "receipt",
    };

    return Object.entries(filterConfig)
      .filter(([key, enabled]) => enabled && keyMapping[key])
      .map(([key, _]) => ({
        label: key,
        key: keyMapping[key],
      }));
  };

  return (
    <Box
      sx={{
        bgcolor: "transparent",
        color: theme.palette.text.primary,
        // p: 3,
        height: "100vh",
      }}
    >
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Transactions",
            value: transactions.summary?.total || 0,
            icon: (
              <img
                src="/Images/hugeicons_transaction.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.primary.main,
          },
          {
            label: "Successful Transactions",
            value: transactions.summary?.successful || 0,
            icon: (
              <img
                src="/Images/mdi_tick-circle-outline.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.success.main,
          },
          {
            label: "Failed Transactions",
            value: transactions.summary?.failed || 0,
            icon: (
              <img
                src="/Images/ix_namur-failure.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.error.main,
          },
          {
            label: "Refunded Transactions",
            value: transactions.summary?.refunded || 0,
            icon: (
              <img
                src="/Images/octicon_blocked-24.png"
                alt="Refunded"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.secondary.main,
          },
        ].map((stat, i) => (
          <Grid item xs={12} md={3} key={i} >
            <StatsCard sx={{
              bgcolor: "transparent", boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(2),
              borderRadius: 2,
            }}>
              <Box>
                <Typography variant="body2" color="text.primary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stat.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: stat.color,
                  width: 40,
                  height: 40,
                  borderRadius: 2, // same 2px rounding
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {stat.icon}
              </Box>
            </StatsCard>
          </Grid>
        ))}
      </Grid>

      {/* Analytics Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: "transparent", border: `1px solid ${theme.palette.divider}`, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Transaction Status</Typography>
            <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {stats.counts ? (
                <ReactApexChart
                  key={`transaction-status-${stats.counts.successful}-${stats.counts.failed}-${stats.counts.refunded}`}
                  options={{
                    chart: { type: 'pie' },
                    labels: ['Successful', 'Failed', 'Refunded'],
                    colors: [theme.palette.success.main, theme.palette.error.main, theme.palette.secondary.main],
                    legend: { position: 'bottom', labels: { colors: theme.palette.text.primary } },
                    stroke: { show: false }
                  }}
                  series={[
                    stats.counts?.successful || 0,
                    stats.counts?.failed || 0,
                    stats.counts?.refunded || 0
                  ]}
                  type="pie"
                  height="100%"
                />
              ) : (
                <CircularProgress size={40} />
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: "transparent", border: `1px solid ${theme.palette.divider}`, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Revenue by Product</Typography>
            <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' , width: '650px' }}>
              {stats.topProducts && stats.topProducts.length > 0 ? (
                <ReactApexChart
                  key={`revenue-by-product-${stats.topProducts.length}`}
                  options={{
                    chart: { type: 'bar', toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 4, horizontal: false } },
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: stats.topProducts?.map(p => p.name || 'Unknown') || [],
                      labels: { style: { colors: theme.palette.text.secondary } }
                    },
                    yaxis: { labels: { style: { colors: theme.palette.text.secondary } } },
                    colors: [theme.palette.success.main],
                    grid: { borderColor: theme.palette.divider },
                    tooltip: {
                      theme: isDarkMode ? 'dark' : 'light',
                      style: {
                        fontSize: '12px',
                        fontFamily: 'inherit',
                      },
                      y: {
                        formatter: (val) => `$${val.toLocaleString()}` 
                      }
                    },
                  }}
                  series={[{
                    name: 'Revenue',
                    data: stats.topProducts?.map(p => p.revenue) || []
                  }]}
                  type="bar"
                  height="100%"
                  width="100%"
                />
              ) : stats.topProducts && stats.topProducts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No revenue data available</Typography>
              ) : (
                <CircularProgress size={40} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Transactions Table */}
      <Box
        sx={{
          bgcolor: "transparent",
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
          borderRadius: 2,
          p: 2
        }}
      >
        {/* Header Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 4,
          }}
        >
          {/* Left group: Title + Rows selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.9,
            }}
          >
            <Typography variant="h4">Transactions</Typography>

            <Typography
              sx={{
                fontSize: "14px",
              }}
              color="text.secondary"
            >
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsChange}
              sx={{
                color: theme.palette.text.primary,
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
                ".MuiSvgIcon-root": { color: theme.palette.text.secondary },
                minWidth: 65,
                height: 25,
              }}
              IconComponent={KeyboardArrowDownIcon}
            >
              {[10, 20, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Right group: Search + Filter + Export */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Search sx={{ width: 300, height: 40 }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Search>

            {/* Filter Icon Button */}

            <Button
              variant="outlined"
              onClick={() => exportMutation.mutate({ format: 'csv', ...getExportParams() })}
              disabled={exportMutation.isLoading}
              sx={{ width: 120 }}
            >
              {exportMutation.isLoading ? 'Exporting...' : 'CSV'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => exportMutation.mutate({ format: 'excel', ...getExportParams() })}
              disabled={exportMutation.isLoading}
              sx={{ width: 120, ml: 1 }}
            >
              {exportMutation.isLoading ? 'Exporting...' : 'Excel'}
            </Button>
            <IconButton
              onClick={handleFilterDialogOpen}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.common.white,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs + Time Filters */}
        <Box
          sx={{
            mt: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {/* Left: Transaction Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            TabIndicatorProps={{ sx: { bgcolor: theme.palette.primary.main } }}
          >
            {["ALL", "SUCCESSFUL", "FAILED", "REFUNDED"].map((label) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                  "&.Mui-selected": { color: theme.palette.primary.main },
                }}
              />
            ))}
          </Tabs>

          {/* Right: Time Filters */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {["All", "Day", "Week", "Month"].map((f) => (
              <TimeFilterButton
                key={f}
                active={timeFilter === f}
                onClick={() => handleTimeChange(f)}
              >
                {f}
              </TimeFilterButton>
            ))}
          </Box>
        </Box>

        {/* Table */}
        <TableContainer sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{
                backgroundColor: isDarkMode
                  ? "#171717"
                  : "background.neutral",
              }}>
                {tableHeaders.map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: "1.2rem",
                      color: theme.palette.text.primary,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontWeight: "bold",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length}>
                    <TableSkeleton
                      animation="wave"
                      variant="rectangular"
                      width="100%"
                      height={40}
                      sx={{ bgcolor: alpha(theme.palette.action.hover, 0.1) }}
                    />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center">
                    <Typography color="error">Failed to load transactions. Please try again.</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center">
                    <Typography>No transactions found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, i) => (
                  <TableRow key={row._id || i} hover>
                    {tableHeaders.map((header) =>
                      renderTableCell(row, header)
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography color="text.secondary">
            Page {page} of {totalPages || 1}
          </Typography>
          <Pagination
            count={totalPages || 1}
            page={page}
            onChange={handlePageChange}
            size="small"
            renderItem={(item) => <PaginationItem {...item} />}
          />
        </Box>
      </Box>

      {/* More menu - Fixed to use theme colors instead of hardcoded black */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            color: theme.palette.text.primary,
            minWidth: "120px",
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
          },
        }}
      >
        <MenuItem
          onClick={() => handleGenerateReceipt(currentRow)}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          View Receipt
        </MenuItem>
        <MenuItem
          onClick={() => handleSendEmailReceipt(currentRow)}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Email Receipt
        </MenuItem>
        <MenuItem
          onClick={() => handleDownloadReceipt(currentRow)}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Download Receipt
        </MenuItem>
      </Menu>

      {/* Filter Dialog - Updated to match the first code */}
      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        PaperProps={{
          sx: {
            position: "absolute",
            bottom: 16,
            right: 16,
            width: "350px",
            maxWidth: "90vw",
            color: theme.palette.text.primary,
            borderRadius: 2,
            m: 0,
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,0.3)" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">Filter Tabs</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleFilterDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {Object.keys(filterConfig).map((key) => (
            <Box key={key}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <Typography variant="body1">{key}</Typography>
                <Switch
                  checked={filterConfig[key]}
                  onChange={() => handleFilterChange(key)}
                  color="primary"
                  size="small"
                />
              </Box>
              <Divider
                sx={{ backgroundColor: alpha(theme.palette.divider, 0.5) }}
              />
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          pt: 4,
          pb: 2,
          position: 'relative'
        }}>
          <IconButton
            onClick={() => setReceiptDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <img
            src="https://cdn.clikkle.com/images/clikkle/logo/2025/Clikkle%20Blue%20(%20White%20Text).png"
            alt="Clikkle"
            style={{ width: 180, marginBottom: 8 }}
          />
          <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: 1, color: '#0048e5' }}>
            PAYMENT RECEIPT
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Thank you for choosing Clikkle
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pb: 4 }}>
          {receiptData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Status Banner */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <StatusChip
                  status={receiptData.status}
                  label={receiptData.status?.toUpperCase()}
                  sx={{ px: 6, py: 2.5, height: 'auto', fontSize: '1rem', fontWeight: '900', borderRadius: '50px' }}
                />
              </Box>

              {/* Header Info */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2.5,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block' }}>DATE</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(receiptData.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block' }}>TRANSACTION ID</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                    {receiptData.transactionId || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 2, fontWeight: '900', borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 }}>
                  PURCHASE SUMMARY
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {receiptData.productName || 'Clikkle Service'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Plan: {receiptData.planName || 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="900" color="primary.main">
                    {receiptData.currency} {receiptData.amount?.toFixed(2)}
                  </Typography>
                </Box>
                {receiptData.description && (
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {receiptData.description}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider />

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block' }}>PAYMENT METHOD</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                      {receiptData.paymentMethod?.replace('_', ' ') || 'Stripe'}
                    </Typography>
                  </Box>
                  {receiptData.card && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {receiptData.card.brand?.toUpperCase()} •••• {receiptData.card.last4}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block' }}>CUSTOMER</Typography>
                  <Typography variant="body2" fontWeight="bold">{receiptData.user?.name || 'Unknown User'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{receiptData.user?.email || 'N/A'}</Typography>
                  {receiptData.user?.recoveryEmail && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                      Recovery: {receiptData.user.recoveryEmail}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Box sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: '#f8faff',
                border: '2px dashed #0048e5',
                textAlign: 'center'
              }}>
                <Typography variant="caption" color="primary.main" fontWeight="bold">TOTAL PAID</Typography>
                <Typography variant="h4" fontWeight="900" color="primary.main">
                  {receiptData.currency} {receiptData.amount?.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  This is a computer generated receipt. No signature required.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    support.clikkle.com
                  </Typography>
                  <Typography variant="body2" color="text.secondary">|</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    clikkle.com
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transaction;
